import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { RollBlankAttachment } from "../models/RollBlank";
import {
	getRollBlankAttachments,
	upsertRollBlankAttachment,
	deleteRollBlankAttachment,
	type UpsertRollBlankAttachmentParams,
} from "../repositories/RollsBlankRepository";
import { ROLLS_BLANK_QUERY_KEYS } from "./useRollsBlanks";

// ============================================
// Query Keys
// ============================================

export const ROLL_BLANK_ATTACHMENTS_QUERY_KEYS = {
	all: ["rollBlankAttachments"] as const,
	byRollBlankId: (rollBlankId: number) =>
		[...ROLL_BLANK_ATTACHMENTS_QUERY_KEYS.all, rollBlankId] as const,
};

// ============================================
// useRollBlankAttachments Hook (Query)
// ============================================

interface UseRollBlankAttachmentsProps {
	rollBlankId: number | null;
	enabled?: boolean;
}

export const useRollBlankAttachments = ({
	rollBlankId,
	enabled = true,
}: UseRollBlankAttachmentsProps) => {
	return useQuery<RollBlankAttachment[], Error>({
		queryKey: ROLL_BLANK_ATTACHMENTS_QUERY_KEYS.byRollBlankId(rollBlankId ?? 0),
		queryFn: () => getRollBlankAttachments(rollBlankId!),
		enabled: enabled && rollBlankId != null,
	});
};

// ============================================
// useRollBlankAttachmentMutations Hook
// ============================================

interface MutationOptions {
	onUploadSuccess?: (data: RollBlankAttachment) => void;
	onDeleteSuccess?: () => void;
	onError?: (error: Error) => void;
}

export const useRollBlankAttachmentMutations = ({
	onUploadSuccess,
	onDeleteSuccess,
	onError,
}: MutationOptions = {}) => {
	const queryClient = useQueryClient();

	const uploadMutation = useMutation({
		mutationFn: async (params: UpsertRollBlankAttachmentParams) => {
			return await upsertRollBlankAttachment(params);
		},
		onSuccess: (data) => {
			queryClient.invalidateQueries({
				queryKey: ROLL_BLANK_ATTACHMENTS_QUERY_KEYS.byRollBlankId(
					data.RollBlankId,
				),
			});
			queryClient.invalidateQueries({
				queryKey: ROLLS_BLANK_QUERY_KEYS.all,
			});
			onUploadSuccess?.(data);
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	const deleteMutation = useMutation({
		mutationFn: async (rollBlankAttachmentId: number) => {
			await deleteRollBlankAttachment(rollBlankAttachmentId);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ROLL_BLANK_ATTACHMENTS_QUERY_KEYS.all,
			});
			queryClient.invalidateQueries({
				queryKey: ROLLS_BLANK_QUERY_KEYS.all,
			});
			onDeleteSuccess?.();
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	return {
		uploadAttachment: uploadMutation.mutate,
		uploadAttachmentAsync: uploadMutation.mutateAsync,
		isUploading: uploadMutation.isPending,
		deleteAttachment: deleteMutation.mutate,
		isDeleting: deleteMutation.isPending,
	};
};
